// Assets.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import "./Assets.css";

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

const getJobStatusTone = (status) => {
  const s = String(status || "").toUpperCase();
  if (s === "COMPLETE") return "complete";
  if (s === "SCHEDULED") return "scheduled";
  if (s === "UNSCHEDULED") return "open";
  return "neutral";
};

const matchesSearch = (row, term, fields) => {
  if (!term) return true;
  const lower = term.toLowerCase();
  return fields
    .map((f) => row[f])
    .filter(Boolean)
    .some((v) => String(v).toLowerCase().includes(lower));
};

const ASSET_SEARCH_FIELDS = [
  "assetName", "assetType", "make", "model", "serial",
  "installDate", "location", "zoneServed", "property", "agreementNumber",
];

const HISTORY_SEARCH_FIELDS = [
  "assetName", "workOrder", "description", "workOrderOutcome", "type", "status",
  "completedDate", "serviceAgreement", "property",
];

/* -------------------------------------------------------------------------- */
/*                              Reusable UI pieces                            */
/* -------------------------------------------------------------------------- */

const KpiCard = ({ label, value, isLoading }) => (
  <div className="assets-kpi-card">
    <div className="assets-kpi-card__label">{label}</div>
    <div className="assets-kpi-card__value">
      {isLoading ? <span className="card-spinner" /> : value}
    </div>
  </div>
);

const SkeletonRows = ({ cols, count = 6 }) =>
  Array.from({ length: count }).map((_, i) => (
    <tr key={i}>
      {Array.from({ length: cols }).map((_, j) => (
        <td key={j}><span className="skeleton-line" /></td>
      ))}
    </tr>
  ));

const CompanySelect = ({ customers, selectedCustomerId, setSelectedCustomerId, disabled }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  useEffect(() => {
    const onClickOutside = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);
  const selected = customers.find((c) => String(c.id) === String(selectedCustomerId));
  const buttonLabel = selected ? selected.name : "All Companies";
  return (
    <div ref={wrapperRef} className="assets-filter-field">
      <label className="assets-filter-field__label">Company Selection</label>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((p) => !p)}
        className="assets-select-btn"
      >
        <span className="assets-select-btn__text">{buttonLabel}</span>
        <span className={`assets-select-btn__chevron ${open ? "is-open" : ""}`}>▾</span>
      </button>
      {open && !disabled ? (
        <div className="assets-multiselect-menu">
          <div className="assets-multiselect-list">
            <div
              className={`assets-checkbox-row${!selectedCustomerId ? " is-selected" : ""}`}
              onClick={() => { setSelectedCustomerId(""); setOpen(false); }}
            >
              <span>All Companies</span>
            </div>
            {customers.map((c) => (
              <div
                key={c.id}
                className={`assets-checkbox-row${String(selectedCustomerId) === String(c.id) ? " is-selected" : ""}`}
                onClick={() => { setSelectedCustomerId(String(c.id)); setOpen(false); }}
              >
                <span>{c.name}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const PropertyMultiSelect = ({
  properties,
  selectedPropertyIds,
  setSelectedPropertyIds,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const onOutside = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  const selectedNames = properties
    .filter((p) => selectedPropertyIds.includes(String(p.id)))
    .map((p) => p.name);

  const buttonLabel = (() => {
    if (!selectedPropertyIds.length) return "Select values";
    if (selectedPropertyIds.length === 1) return selectedNames[0] || "1 Property";
    return `${selectedPropertyIds.length} selected`;
  })();

  const toggle = (id) => {
    setSelectedPropertyIds((curr) => {
      const s = String(id);
      return curr.includes(s) ? curr.filter((x) => x !== s) : [...curr, s];
    });
  };

  return (
    <div ref={wrapperRef} className="assets-filter-field">
      <label className="assets-filter-field__label">Property Selection</label>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((p) => !p)}
        className="assets-select-btn"
      >
        <span className="assets-select-btn__text">{buttonLabel}</span>
        <span className={`assets-select-btn__chevron ${open ? "is-open" : ""}`}>▾</span>
      </button>

      {open && !disabled ? (
        <div className="assets-multiselect-menu">
          <div className="assets-multiselect-actions">
            <button type="button" className="assets-pill-btn" onClick={() => setSelectedPropertyIds([])}>
              All
            </button>
            <button
              type="button"
              className="assets-pill-btn"
              onClick={() => setSelectedPropertyIds(properties.map((p) => String(p.id)))}
            >
              Select All
            </button>
          </div>
          <div className="assets-multiselect-list">
            {properties.length ? (
              properties.map((p) => (
                <label key={p.id} className="assets-checkbox-row">
                  <input
                    type="checkbox"
                    checked={selectedPropertyIds.includes(String(p.id))}
                    onChange={() => toggle(p.id)}
                  />
                  <span>{p.name}</span>
                </label>
              ))
            ) : (
              <div className="assets-multiselect-empty">No properties available.</div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*                                   Assets                                   */
/* -------------------------------------------------------------------------- */

const emptyData = {
  kpis: { totalAssets: 0, assetsOnOpenWorkOrders: 0 },
  assets: [],
  workHistory: [],
};

const Assets = ({ viewerEmail }) => {
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [properties, setProperties] = useState([]);

  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [selectedPropertyIds, setSelectedPropertyIds] = useState([]);

  const [filterError, setFilterError] = useState("");
  const [dataError, setDataError] = useState("");

  const [data, setData] = useState(emptyData);

  const [assetSearch, setAssetSearch] = useState("");
  const [historySearch, setHistorySearch] = useState("");
  const [selectedAsset, setSelectedAsset] = useState(null);

  // Load filters
  useEffect(() => {
    const load = async () => {
      try {
        setFiltersLoading(true);
        setFilterError("");
        const token = localStorage.getItem("token");
        if (!token) { setFilterError("No auth token found."); return; }

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/assets/assets-filters`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: viewerEmail ? { email: viewerEmail } : undefined,
          }
        );
        const d = res?.data || {};
        setCustomers(Array.isArray(d.customers) ? d.customers : []);
        setProperties(Array.isArray(d.properties) ? d.properties : []);
      } catch (err) {
        console.error("Failed to load assets filters:", err);
        setFilterError("Failed to load filters.");
      } finally {
        setFiltersLoading(false);
      }
    };
    load();
  }, [viewerEmail]);

  const filteredProperties = useMemo(() => {
    if (!selectedCustomerId) return properties;
    return properties.filter(
      (p) => String(p.customerId ?? "") === String(selectedCustomerId)
    );
  }, [properties, selectedCustomerId]);

  useEffect(() => {
    const allowed = new Set(filteredProperties.map((p) => String(p.id)));
    setSelectedPropertyIds((curr) => curr.filter((id) => allowed.has(id)));
  }, [filteredProperties]);

  // Load data
  useEffect(() => {
    const load = async () => {
      if (filtersLoading || filterError) return;
      try {
        setDataLoading(true);
        setDataError("");
        const token = localStorage.getItem("token");
        if (!token) { setDataError("No auth token found."); return; }

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/assets/assets-data`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              ...(viewerEmail ? { email: viewerEmail } : {}),
              ...(selectedCustomerId ? { customerId: selectedCustomerId } : {}),
              ...(selectedPropertyIds.length
                ? { propertyIds: selectedPropertyIds.join(",") }
                : {}),
            },
          }
        );
        setData(res?.data || emptyData);
      } catch (err) {
        console.error("Failed to load assets data:", err);
        setDataError("Failed to load assets data.");
        setData(emptyData);
      } finally {
        setDataLoading(false);
      }
    };
    load();
  }, [filtersLoading, filterError, viewerEmail, selectedCustomerId, selectedPropertyIds]);

  const visibleAssets = useMemo(
    () => (data.assets || []).filter((r) => matchesSearch(r, assetSearch, ASSET_SEARCH_FIELDS)),
    [data.assets, assetSearch]
  );

  const [visibleHistory, setVisibleHistory] = useState([]);
  useEffect(() => {
    let rows = data.workHistory || [];
    if (selectedAsset) {
      const needle = selectedAsset.toLowerCase().trim();
      rows = rows.filter((r) => (r.assetName || "").toLowerCase().trim() === needle);
    }
    setVisibleHistory(rows.filter((r) => matchesSearch(r, historySearch, HISTORY_SEARCH_FIELDS)));
  }, [data.workHistory, selectedAsset, historySearch]);

  const handleClear = () => {
    setSelectedCustomerId("");
    setSelectedPropertyIds([]);
    setAssetSearch("");
    setHistorySearch("");
    setSelectedAsset(null);
  };

  const isLoading = filtersLoading || dataLoading;

  return (
    <div className="assets-page">
      <div className="assets-shell">
        {/* ---- Filter bar ---- */}
        <section className="assets-toolbar">
          <div className="assets-toolbar__grid">
            <CompanySelect
              customers={customers}
              selectedCustomerId={selectedCustomerId}
              setSelectedCustomerId={setSelectedCustomerId}
              disabled={filtersLoading || !!filterError}
            />

            <PropertyMultiSelect
              properties={filteredProperties}
              selectedPropertyIds={selectedPropertyIds}
              setSelectedPropertyIds={setSelectedPropertyIds}
              disabled={filtersLoading || !!filterError}
            />

            <div className="assets-filter-field">
              <label className="assets-filter-field__label">Search Assets</label>
              <input
                type="text"
                value={assetSearch}
                onChange={(e) => setAssetSearch(e.target.value)}
                className="assets-search-input"
                placeholder=""
              />
            </div>

            <div className="assets-toolbar__clear">
              <button type="button" onClick={handleClear} className="assets-clear-btn">
                Clear Search
              </button>
            </div>
          </div>
        </section>

        {/* ---- Feedback ---- */}
        {filterError ? (
          <div className="assets-feedback assets-feedback--error">{filterError}</div>
        ) : null}
        {dataError ? (
          <div className="assets-feedback assets-feedback--error">{dataError}</div>
        ) : null}

        {!filterError ? (
          <>
            {/* ---- KPI cards ---- */}
            <section className="assets-kpi-grid">
              <KpiCard
                label="Assets"
                value={data.kpis?.totalAssets ?? 0}
                isLoading={isLoading}
              />
              <KpiCard
                label="Assets on Open Work Orders"
                value={data.kpis?.assetsOnOpenWorkOrders ?? 0}
                isLoading={isLoading}
              />
            </section>

            {/* ---- Assets table ---- */}
            <section className="assets-table-card">
              <div className="assets-table-card__header">
                <span className="assets-table-card__title">Assets</span>
                <span className="assets-table-card__meta">
                  {visibleAssets.length} items
                </span>
              </div>

              <div className="assets-table-wrap">
                {isLoading || visibleAssets.length > 0 ? (
                  <table className="assets-table">
                    <thead>
                      <tr>
                        <th>Asset Name</th>
                        <th>Make</th>
                        <th>Model</th>
                        <th>Serial #</th>
                        <th>Asset Date</th>
                        <th>Type</th>
                        <th>Location</th>
                        <th>Zone Served</th>
                        <th>Property</th>
                        <th>Agreement #</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? <SkeletonRows cols={10} /> : visibleAssets.map((row, idx) => {
                        const isSelected = selectedAsset === row.assetName;
                        return (
                        <tr
                          key={row.assetId || `asset-${idx}`}
                          className={`${idx % 2 === 0 ? "" : "is-alt"} ${isSelected ? "is-selected" : ""}`}
                          style={{ cursor: "pointer" }}
                          onClick={() => setSelectedAsset(isSelected ? null : row.assetName)}
                        >
                          <td><div className="assets-primary-cell">{row.assetName || "—"}</div></td>
                          <td>{row.make || "—"}</td>
                          <td>{row.model || "—"}</td>
                          <td>{row.serial || "—"}</td>
                          <td>{row.installDate || "—"}</td>
                          <td>{row.assetType || "—"}</td>
                          <td>{row.location || "—"}</td>
                          <td>{row.zoneServed || "—"}</td>
                          <td>{row.property || "—"}</td>
                          <td>{row.agreementNumber || "—"}</td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="assets-empty-state">No assets found</div>
                )}
              </div>
            </section>

            {/* ---- All Work History table ---- */}
            <section className="assets-table-card assets-table-card--history">
              <div className="assets-table-card__header">
                <div className="assets-table-card__title-group">
                  <span className="assets-table-card__title">
                    {selectedAsset ? `${selectedAsset} Work History` : "All Work History"}
                  </span>
                  {selectedAsset ? (
                    <button
                      type="button"
                      className="assets-clear-asset-btn"
                      onClick={() => setSelectedAsset(null)}
                    >
                      Clear Filter
                    </button>
                  ) : null}
                </div>
                <div className="assets-table-card__header-right">
                  <span className="assets-table-card__meta">
                    {visibleHistory.length} items
                  </span>
                  <input
                    type="text"
                    value={historySearch}
                    onChange={(e) => setHistorySearch(e.target.value)}
                    className="assets-inline-search"
                    placeholder="Search work history…"
                  />
                </div>
              </div>

              <div className="assets-table-wrap">
                {isLoading || visibleHistory.length > 0 ? (
                  <table className="assets-table">
                    <thead>
                      <tr>
                        <th>Work Order</th>
                        <th>Asset Name</th>
                        <th>Description</th>
                        <th>Work Order Outcome</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Completed Date</th>
                        <th>Service Agreement</th>
                        <th>Property</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? <SkeletonRows cols={9} /> : visibleHistory.map((row, idx) => (
                        <tr key={`${row.jobId}-${row.assetName}-${idx}`} className={idx % 2 === 0 ? "" : "is-alt"}>
                          <td><div className="assets-primary-cell">{!row.workOrder ? "—" : row.reportUrl ? (<a href={row.reportUrl} target="_blank" rel="noopener noreferrer" className="assets-primary-link">{row.workOrder}</a>) : row.workOrder}</div></td>
                          <td>{row.assetName || "—"}</td>
                          <td className="assets-description-cell">{row.description || "—"}</td>
                          <td className="assets-description-cell">{row.workOrderOutcome || "—"}</td>
                          <td>{row.type || "—"}</td>
                          <td>
                            <span className={`assets-status-pill assets-status-pill--${getJobStatusTone(row.status)}`}>
                              {row.status || "—"}
                            </span>
                          </td>
                          <td>{row.completedDate || "—"}</td>
                          <td>{row.serviceAgreement || "—"}</td>
                          <td>{row.property || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="assets-empty-state">No work history found</div>
                )}
              </div>
            </section>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default Assets;
